import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { isPlatformBrowser } from '@angular/common';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { ChartData } from 'src/shared/models/chart.model';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import { extractYearFromString } from 'src/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { TranslateDataKeysService } from 'src/shared/services/translate-data-keys.service';

@Component({
  selector: 'atom-partitioned-bar-chart',
  templateUrl: './partitioned-bar-chart.component.html',
  styleUrls: ['./partitioned-bar-chart.component.scss'],
})
export class PartitionedBarChartComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private root!: am5.Root | undefined;
  private chart!: am5xy.XYChart;
  private legend!: am5.Legend;
  public exporting!: am5plugins_exporting.Exporting;

  @Input() chartData: ChartData[] = [];
  @Input() categoryField: string = '';
  @Input() valueField: string = '';
  @Input() regionField: string = '';
  @Input() chartId: string =
    'partitioned-bar-chart-' + Math.random().toString(36).substr(2, 9);
  @Input() language: string = '';
  @Input() xAxisTitle?: string;
  @Input() yAxisTitle?: string;
  @Input() dataFields: string[] = []; // NUEVO: campos de series
  @Input() seriesNames: string[] = []; // NUEVO: nombres personalizados para las series
  @Input() colors: string[] = ['#16726b', '#c7e4d7']; // Solo los colores usados en la app
  @Input() rotateXAxisLabels?: boolean = false; // Indica si las etiquetas del eje Y deben rotarse para mejorar su legibilidad
  @Input() fileName: string = ''; // Nombre del archivo al exportar
  @Input() percentageField: string = ''; // Campo de porcentaje

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private translateService: TranslateService,
    private translateDataKeysService: TranslateDataKeysService,
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.browserOnly(() => {
      this.root = am5.Root.new(this.chartId);
      this.renderChart();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.root && (changes['chartData'] || changes['language'])) {
      this.root.dispose();
      this.root = undefined;
      this.browserOnly(() => {
        this.root = am5.Root.new(this.chartId);
        this.renderChart();
      });
    }
  }

  ngOnDestroy() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (this.root) {
      this.chart.markDirtySize(); // Notifica al gráfico que el tamaño de la ventana ha cambiado
      this.chart.series.each((series) => {
        series.appear(); // Reaplica las animaciones para ajustar la posición de los elementos según el nuevo tamaño de la ventana
      });
      this.updateLegend(); // Actualiza la leyenda del gráfico cuando cambia el tamaño de la ventana
    }
  }

  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  private renderChart() {
    if (!this.root) return;
    const root = this.root; // Solución: variable local no undefined
    root.setThemes([
      am5themes_Animated.new(root),
      am5themes_Responsive.new(root),
    ]);

    this.chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingLeft: 0,
      }),
    );

    // Eje Y (categorías)
    let yAxis = this.chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: this.categoryField,
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 10,
          minorGridEnabled: true,
        }),
      }),
    );
    yAxis.get('renderer').labels.template.setAll({
      fontSize: 12,
      location: 0.5,
    });

    // Rotar las etiquetas en el eje Y si está habilitado
    if (this.rotateXAxisLabels) {
      yAxis.get('renderer').labels.template.setAll({
        rotation: -45, // Ángulo en grados (-45 para diagonal)
      });
    }
    yAxis.data.setAll(this.chartData);

    // Eje X (valores)
    let xAxis = this.chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {}),
      }),
    );

    // SERIES AGRUPADAS
    const colors =
      this.colors && this.colors.length > 0
        ? this.colors
        : ['#16726b', '#c7e4d7'];
        
    this.dataFields.forEach((field, idx) => {
      let seriesName = field;

      // Si hay nombres personalizados de series, usarlos
      if (this.seriesNames && this.seriesNames[idx]) {
        const rawName = this.seriesNames[idx];

        // Si el nombre personalizado tiene un pipe, se implementa traducción
        if (rawName.includes('|')) {
          const [key, paramsStr] = rawName.split('|');
          try {
            const params = JSON.parse(paramsStr.trim());
            seriesName = this.translateService.instant(key.trim(), params);
          } catch (e) {
            seriesName = this.translateService.instant(key.trim());
          }
        } else {
          // Si no tiene pipe, se queda igual (asignación directa)
          seriesName = rawName;
        }
      } else {
        // Traducción de la serie sin nombres personalizados
        const yearMatch = extractYearFromString(seriesName);
        let nameBase = yearMatch
          ? seriesName.replace(yearMatch, '').trim()
          : seriesName; // Elimina el año, si no encuentra año, asigna el nombre de la serie original
        seriesName = this.translateService.instant(`CHARTS.${nameBase}`);

        if (seriesName === `CHARTS.${nameBase}`) {
          seriesName = nameBase;
        }

        // Traducción de la serie
        if (seriesName) {
          // Añadir el año al final del nombre traducido, si hay un año encontrado
          if (yearMatch) {
            const year = field.match(yearMatch)?.[0]; // Obtiene el año encontrado en la cadena
            if (year) {
              seriesName += ` ${year}`; // Añade el año al final del nombre traducido
            }
          }
        }
      }

      let series = this.chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: seriesName, // nombre único para la leyenda
          xAxis: xAxis,
          yAxis: yAxis,
          valueXField: field,
          categoryYField: this.categoryField,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText: `{name}\n{categoryY}: [bold]{valueX}[/]`,
          }),
        }),
      );
      // Forzar el nombre en la serie para la leyenda
      series.set('name', seriesName);
      series.columns.template.setAll({
        width: am5.percent(90 / this.dataFields.length),
        fill: am5.color(colors[idx % colors.length]),
        strokeOpacity: 0,
      });

      series.data.setAll(this.chartData);
      series.appear(2000);
    });

    // Mostrar etiquetas de porcentaje al final de cada barra
    this.chartData.forEach((dataItem, index) => {
      const percentageValue = (dataItem as any)[this.percentageField];
      
      if (percentageValue !== undefined && percentageValue !== null) {
        const labelText = String(percentageValue);
        const reversedIndex = this.chartData.length - 1 - index;
        this.chart.plotContainer.children.push(
          am5.Label.new(root, {
            text: labelText,
            x: am5.percent(98),
            centerX: am5.p100,
            y: am5.percent((reversedIndex + 0.5) * (100 / this.chartData.length)),
            centerY: am5.p50,
            fill: am5.color(labelText.startsWith('-') ? '#d9534f' : '#16726b'),
            fontSize: 13,
            fontWeight: '500'
          })
        );
      }
    });

    // Leyenda centrada y funcional (permite ocultar/mostrar series)
    this.legend = this.chart.children.push(
      am5.Legend.new(root, {
        nameField: 'name',
        fillField: 'fill',
        strokeField: 'fill',
        x: am5.p50, // Centrado horizontalmente
        centerX: am5.p50, // Asegura el centrado horizontal
        centerY: am5.p50, // Asegura el centrado vertical
        layout:
          window.innerWidth <= 1024
            ? root.verticalLayout
            : root.horizontalLayout,
        // clickTarget por defecto permite ocultar/mostrar series
      }),
    );
    this.legend.data.setAll(this.chart.series.values);

    // Aplicar el layout responsive inicial
    this.updateLegend();

    // Cursor
    let cursor = this.chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
      }),
    );

    // Animaciones
    this.chart.appear(2000, 100);

    // Exportación
    this.exporting = am5plugins_exporting.Exporting.new(root, {
      filePrefix: this.fileName,
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
      dataSource: this.translateDataKeysService.translateKeys(
        this.chartData,
        'CHARTS',
      ), // Especifica los datos que se exportarán, usando 'chartData' como fuente
      htmlOptions: { disabled: true },
      jsonOptions: { disabled: true },
    });
  }

  private updateLegend() {
    if (!this.root) return;

    // Actualiza la leyenda en función del tamaño de la ventana
    if (window.innerWidth <= 1024) {
      this.legend.set('layout', this.root.verticalLayout); // Cambiar la disposición a vertical para anchos de ventana menores a 1024px
    } else {
      this.legend.set('layout', this.root.horizontalLayout); // Cambiar la disposición a horizontal para anchos de ventana mayores a 1024px
    }
  }
}
